/**
 * use-accounts — reactive view over the web multi-account registry + the
 * switch/add/remove orchestration used by the account switcher UI.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import {
  ACCOUNTS_STORAGE_KEY,
  type AccountsState,
  getActiveAccount,
  type StoredAccount,
} from "@time-tracker/shared";
import { accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  ACCOUNTS_CHANGED_EVENT,
  activeTabAccountId,
  clearTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();

    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCOUNTS_STORAGE_KEY || e.key === null) {
        reload();
      }
    };

    const onLocalAccountsChange = () => {
      reload();
    };

    const onTabAccountChange = () => {
      reload();
    };

    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalAccountsChange);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onTabAccountChange);

    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalAccountsChange);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onTabAccountChange);
    });
  });

  const accounts = () => state().accounts;

  const active = (): StoredAccount | null => resolveTabAccount(state(), activeTabAccountId());
  const activeUserId = () => active()?.userId ?? null;

  /**
   * Switches THIS TAB to a known account WITHOUT clearing local SQLite data.
   */
  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);

    // D1/D2: Preserve current route shape on account switch.
    // If the matched route has entity params ($), fall back to section ancestor.
    const currentMatches = router?.state?.matches;
    const currentMatch =
      currentMatches?.findLast?.((m) => Boolean(m.fullPath)) ??
      (currentMatches && currentMatches.length > 0 ? currentMatches.at(-1) : undefined);
    const fullPath = currentMatch?.fullPath;
    if (fullPath && fullPath.includes("$")) {
      const ancestor = fullPath.split("$")[0]?.replace(/\/+$/, "");
      await navigate({ to: ancestor || "/" });
    }
  };

  /**
   * Switches THIS TAB to another account and STAYS on the current URL.
   */
  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  /**
   * Opens the login screen in "add" mode.
   */
  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  /**
   * Activates the explicit guest account.
   */
  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    if (!guest) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(guest);
    writeTabAccountId(guest.userId);
    await navigate({ to: "/" });
  };

  /**
   * Removes an account from the registry and destroys its local database.
   */
  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeUserId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        writeTabAccountId(fallback.userId);
      } else {
        clearTabAccountId();
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
